/**
 * Memoization utility for mapper factory functions.
 * Caches the results of expensive operations like config parsing and validation.
 */

/**
 * Creates a memoized version of a mapper factory function.
 * The memoization key is generated from the config object.
 * 
 * @param fn The mapper factory function to memoize
 * @returns A memoized version of the function
 */
export function memoizeMapperFactory<TConfig, TReturn>(
  fn: (config: TConfig) => TReturn
): (config: TConfig) => TReturn {
  const cache = new Map<string, TReturn>();
  
  return (config: TConfig): TReturn => {
    // Generate a stable cache key from the config
    // Using JSON.stringify for simplicity, but could be optimized
    const cacheKey = JSON.stringify(config);
    
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey)!;
    }
    
    const result = fn(config);
    cache.set(cacheKey, result);
    
    // Limit cache size to prevent memory leaks
    if (cache.size > 100) {
      // Remove the oldest entry
      const firstKey = cache.keys().next().value as string;
      cache.delete(firstKey);
    }
    
    return result;
  };
}

/**
 * Clears all memoization caches. Useful for testing.
 */
export function clearMemoizationCaches(): void {
  // Since we're using closures, we'd need to track all caches globally
  // For now, this is a placeholder that could be expanded if needed
  console.warn("clearMemoizationCaches not fully implemented - caches are cleared on module reload");
}